import { EventManager } from '../core/EventManager';
import { Logger } from '../core/Logger';
import { InputEventManager } from '../core/InputEventManager';

/**
 * InputManager - Handles all keyboard and mouse input events
 * 
 * Extracted from EngineDemoApp to reduce complexity and improve maintainability.
 * Centralizes input handling and provides a clean interface for the main app.
 */
export class InputManager {
    private eventManager: EventManager;
    private keydownHandler: (event: KeyboardEvent) => void;
    private keyupHandler: (event: KeyboardEvent) => void;
    private clickHandler: (event: MouseEvent) => void;
    private doubleClickHandler: (event: MouseEvent) => void;
    private mouseMoveHandler: (event: MouseEvent) => void;
    private wheelHandler: (event: WheelEvent) => void;
    private touchStartHandler: (event: TouchEvent) => void;
    private touchMoveHandler: (event: TouchEvent) => void;
    private touchEndHandler: (event: TouchEvent) => void;
    private isEnabled: boolean = true;
    private _lastPinchDistance: number = 0;
    private _isPinching: boolean = false;
    
    // Key mapping for actions
    private keyMappings: { [key: string]: string } = {
        'ArrowLeft': 'navigate-previous',
        'ArrowRight': 'navigate-next',
        'ArrowUp': 'zoom-in',
        'ArrowDown': 'zoom-out',
        'F1': 'toggle-stats',
        'F2': 'toggle-performance',
        // 'F3': 'toggle-html-rendering', // Removed - F3 now used for Settings Quick Finder in UI
        'F4': 'toggle-debug',
        'F5': 'toggle-field-awareness',
        'F6': 'toggle-field-direction-animation',
        'F7': 'show-poi-list',
        // 'F8': 'clear-pois', // Removed - F8 now used for Parser Test in EngineDemoUI
        'F9': 'cycle-layout',
        'F10': 'toggle-wireframe',
        'F11': 'toggle-fullscreen',
        'F12': 'export-scene',
        'Space': 'toggle-pause',
        'Escape': 'close-dialogs',
        'Delete': 'delete-selected',
        'Backspace': 'delete-selected',
        'Enter': 'confirm-action',
        'Tab': 'cycle-selection',
        'Home': 'go-to-start',
        'End': 'go-to-end',
        'PageUp': 'previous-page',
        'PageDown': 'next-page',
        'Insert': 'add-poi',
        'KeyR': 'reset-camera',
        'KeyT': 'toggle-tooltips',
        'KeyG': 'toggle-grid',
        'KeyL': 'toggle-lighting',
        'KeyM': 'toggle-materials',
        'KeyN': 'set-layouter-line',
        'KeyP': 'toggle-physics',
        'KeyS': 'save-scene',
        'KeyO': 'load-scene',
        'KeyQ': 'apple-quicklook-ar',
        'KeyV': 'toggle-ar',
        'KeyW': 'move-forward',
        'KeyA': 'move-left',
        'KeyD': 'move-right',
        'KeyX': 'move-backward',
        'KeyE': 'move-up',
        'KeyC': 'move-down',
        'Digit1': 'set-shape-cube',
        'Digit2': 'set-shape-sphere',
        'Digit3': 'set-shape-cylinder',
        'Digit4': 'set-shape-plane',
        'Digit5': 'set-shape-triangle',
        'Digit6': 'set-shape-hexagon',
        'Digit7': 'set-shape-torus',
        'Digit8': 'set-shape-custom',
        'Digit9': 'set-shape-random',
        'Digit0': 'set-shape-default'
    };
    
    constructor(eventManager: EventManager) {
        this.eventManager = eventManager;
        
        // Bind event handlers
        this.keydownHandler = this.handleKeyDown.bind(this);
        this.keyupHandler = this.handleKeyUp.bind(this);
        this.clickHandler = this.handleClick.bind(this);
        this.doubleClickHandler = this.handleDoubleClick.bind(this);
        this.mouseMoveHandler = this.handleMouseMove.bind(this);
        this.wheelHandler = this.handleWheel.bind(this);
        this.touchStartHandler = this.handleTouchStart.bind(this);
        this.touchMoveHandler = this.handleTouchMove.bind(this);
        this.touchEndHandler = this.handleTouchEnd.bind(this);

        this.setupEventListeners();
        
        //Logger.Instance.Info('⌨️ InputManager initialized', 'InputManager');
    }
    
    // ==================== EVENT LISTENER SETUP ====================
    
    /**
     * Setup all event listeners
     */
    private setupEventListeners(): void {
        // Keyboard events
        document.addEventListener('keydown', this.keydownHandler);
        document.addEventListener('keyup', this.keyupHandler);
        
        // Mouse events
        document.addEventListener('click', this.clickHandler);
        document.addEventListener('dblclick', this.doubleClickHandler);
        document.addEventListener('mousemove', this.mouseMoveHandler);
        document.addEventListener('wheel', this.wheelHandler, { passive: false });

        // Touch events for pinch-to-zoom
        document.addEventListener('touchstart', this.touchStartHandler, { passive: false });
        document.addEventListener('touchmove', this.touchMoveHandler, { passive: false });
        document.addEventListener('touchend', this.touchEndHandler);
    }
    
    /**
     * Remove all event listeners
     */
    private removeEventListeners(): void {
        document.removeEventListener('keydown', this.keydownHandler);
        document.removeEventListener('keyup', this.keyupHandler);
        document.removeEventListener('click', this.clickHandler);
        document.removeEventListener('dblclick', this.doubleClickHandler);
        document.removeEventListener('mousemove', this.mouseMoveHandler);
        document.removeEventListener('wheel', this.wheelHandler);
        document.removeEventListener('touchstart', this.touchStartHandler);
        document.removeEventListener('touchmove', this.touchMoveHandler);
        document.removeEventListener('touchend', this.touchEndHandler);
        
        //Logger.Instance.Info('🔗 Input event listeners removed', 'InputManager');
    }
    
    // ==================== EVENT HANDLERS ====================
    
    /**
     * Handle keyboard keydown events
     */
    private handleKeyDown(event: KeyboardEvent): void {
        if (!this.isEnabled) return;
        
        // 🛡️ CRITICAL: Don't intercept keys when user is typing in text fields
        if (this.isTextInputFocused()) {
            return; // Let the browser handle text input normally
        }
        
        const key = event.code;
        const action = this.keyMappings[key];
        
        // Special handling for Ctrl+K / Cmd+K (spectator camera toggle)
        if (key === 'KeyK' && (event.ctrlKey || event.metaKey)) {
            event.preventDefault();
            
            const inputEvent = {
                type: 'keydown',
                key: key,
                action: 'toggle-spectator-camera',
                modifiers: {
                    ctrl: event.ctrlKey,
                    shift: event.shiftKey,
                    alt: event.altKey,
                    meta: event.metaKey
                },
                originalEvent: event
            };
            
            //Logger.Instance.Debug(`🎹 Key pressed: ${key} + Ctrl/Cmd -> toggle-spectator-camera`, 'InputManager');
            this.eventManager.emit('input:action', inputEvent);
            return;
        }
        
        if (action) {
            event.preventDefault();
            
            // Create input event with modifier keys
            const inputEvent = {
                type: 'keydown',
                key: key,
                action: action,
                modifiers: {
                    ctrl: event.ctrlKey,
                    shift: event.shiftKey,
                    alt: event.altKey,
                    meta: event.metaKey
                },
                originalEvent: event
            };
            
            //Logger.Instance.Debug(`🎹 Key pressed: ${key} -> ${action}`, 'InputManager');
            this.eventManager.emit('input:action', inputEvent);
        }
    }
    
    /**
     * Check if a text input element currently has focus
     */
    private isTextInputFocused(): boolean {
        const activeElement = document.activeElement;
        if (!activeElement) return false;
        
        const tagName = activeElement.tagName.toLowerCase();
        const inputType = (activeElement as HTMLInputElement).type?.toLowerCase();
        
        // Check for text input elements
        return (
            tagName === 'input' && (
                inputType === 'text' || 
                inputType === 'password' || 
                inputType === 'email' || 
                inputType === 'search' || 
                inputType === 'tel' || 
                inputType === 'url' || 
                inputType === 'number' ||
                !inputType // Default input type is text
            )
        ) || 
        tagName === 'textarea' ||
        tagName === 'select' ||
        (activeElement as HTMLElement).contentEditable === 'true';
    }

    /**
     * Handle keyboard keyup events
     */
    private handleKeyUp(event: KeyboardEvent): void {
        if (!this.isEnabled) return;
        
        const key = event.code;
        const action = this.keyMappings[key];
        
        if (action) {
            // Create input event
            const inputEvent = {
                type: 'keyup',
                key: key,
                action: action,
                modifiers: {
                    ctrl: event.ctrlKey,
                    shift: event.shiftKey,
                    alt: event.altKey,
                    meta: event.metaKey
                },
                originalEvent: event
            };
            
            this.eventManager.emit('input:action-end', inputEvent);
        }
    }
    
    /**
     * Handle mouse click events
     */
    private handleClick(event: MouseEvent): void {
        if (!this.isEnabled) return;
        
        const clickEvent = {
            type: 'click',
            x: event.clientX,
            y: event.clientY,
            button: event.button,
            modifiers: {
                ctrl: event.ctrlKey,
                shift: event.shiftKey,
                alt: event.altKey,
                meta: event.metaKey
            },
            originalEvent: event
        };
        
        //Logger.Instance.Debug(`🖱️ Mouse click: (${event.clientX}, ${event.clientY}) button: ${event.button}`, 'InputManager');
        this.eventManager.emit('input:click', clickEvent);
    }
    
    /**
     * Handle mouse double-click events
     */
    private handleDoubleClick(event: MouseEvent): void {
        if (!this.isEnabled) return;
        
        const doubleClickEvent = {
            type: 'doubleclick',
            x: event.clientX,
            y: event.clientY,
            button: event.button,
            modifiers: {
                ctrl: event.ctrlKey,
                shift: event.shiftKey,
                alt: event.altKey,
                meta: event.metaKey
            },
            originalEvent: event
        };
        
        //Logger.Instance.Debug(`🖱️ Mouse double-click: (${event.clientX}, ${event.clientY})`, 'InputManager');
        this.eventManager.emit('input:doubleclick', doubleClickEvent);
    }
    
    /**
     * Handle mouse move events
     */
    private handleMouseMove(event: MouseEvent): void {
        if (!this.isEnabled) return;
        
        const mouseMoveEvent = {
            type: 'mousemove',
            x: event.clientX,
            y: event.clientY,
            movementX: event.movementX,
            movementY: event.movementY,
            modifiers: {
                ctrl: event.ctrlKey,
                shift: event.shiftKey,
                alt: event.altKey,
                meta: event.metaKey
            },
            originalEvent: event
        };
        
        // Emit mouse move event (throttled to avoid spam)
        this.eventManager.emit('input:mousemove', mouseMoveEvent);
    }
    
    /**
     * Handle mouse wheel events (BACKUP - for reliable zoom)
     */
    private handleWheel(event: WheelEvent): void {
        if (!this.isEnabled) return;

        // Skip wheel events on UI overlays (Settings dialog, toolbar, modals, etc.).
        // Camera zoom should only fire when the cursor is over the 3D canvas, not over an overlay.
        const target = event.target as HTMLElement | null;
        if (target && target.closest && !target.closest('canvas')) {
            return;
        }

        // Directly update InputEventManager with wheel data
        const wheelDelta = event.deltaY; // Changed: positive deltaY = zoom in, negative = zoom out (more intuitive)
        Logger.Instance.Info(`wheel deltaY=${event.deltaY.toFixed(2)} ctrlKey=${event.ctrlKey}`, 'Wheel');
        InputEventManager.Instance.mouseWheelDelta = wheelDelta;
        
        //Logger.Instance.Debug(`🎡 Direct wheel event: deltaY=${event.deltaY}, wheelDelta=${wheelDelta}`, 'InputManager');
        
        // Emit wheel event
        const wheelEvent = {
            type: 'wheel',
            deltaX: event.deltaX,
            deltaY: event.deltaY,
            deltaZ: event.deltaZ,
            wheelDelta: wheelDelta,
            modifiers: {
                ctrl: event.ctrlKey,
                shift: event.shiftKey,
                alt: event.altKey,
                meta: event.metaKey
            },
            originalEvent: event
        };
        
        this.eventManager.emit('input:wheel', wheelEvent);
    }

    // ==================== TOUCH / PINCH-TO-ZOOM ====================

    private getTouchDistance(t1: Touch, t2: Touch): number {
        const dx = t1.clientX - t2.clientX;
        const dy = t1.clientY - t2.clientY;
        return Math.sqrt(dx * dx + dy * dy);
    }

    /** Zeitpunkt/Ort des letzten Ein-Finger-Taps fuer die Double-Tap-Erkennung. */
    private _lastTapTime = 0;
    private _lastTapX = 0;
    private _lastTapY = 0;

    private handleTouchStart(event: TouchEvent): void {
        if (!this.isEnabled) return;
        Logger.Instance.Info(`Touch start: ${event.touches.length} fingers`, 'Touch');
        Logger.Instance.forceFlush();

        // Double-Tap: zwei Ein-Finger-Taps kurz nacheinander am selben Ort.
        // Das DOM-dblclick-Event feuert auf iOS-Touch nicht zuverlaessig — hier
        // selbst erkennen und in dieselbe Pipeline geben wie der Desktop-Doppelklick
        // (POI-Aktivierung via handleInputDoubleClick, generisch fuer jede Shape).
        if (event.touches.length === 1) {
            const t = event.touches[0];
            const now = performance.now();
            const dx = t.clientX - this._lastTapX;
            const dy = t.clientY - this._lastTapY;
            if (now - this._lastTapTime < 350 && (dx * dx + dy * dy) < 35 * 35) {
                this._lastTapTime = 0;
                event.preventDefault(); // iOS-Doppeltipp-Browserzoom unterdruecken
                this.eventManager.emit('input:doubleclick', {
                    type: 'doubleclick',
                    x: t.clientX,
                    y: t.clientY,
                    button: 0,
                    modifiers: { ctrl: false, shift: false, alt: false, meta: false },
                    originalEvent: event
                });
            } else {
                this._lastTapTime = now;
                this._lastTapX = t.clientX;
                this._lastTapY = t.clientY;
            }
        }

        if (event.touches.length === 2) {
            this._isPinching = true;
            this._lastPinchDistance = this.getTouchDistance(event.touches[0], event.touches[1]);
            Logger.Instance.Info(`Pinch start: distance=${this._lastPinchDistance.toFixed(0)}`, 'Touch');
            Logger.Instance.forceFlush();
            event.preventDefault();
        }
    }

    private handleTouchMove(event: TouchEvent): void {
        if (!this.isEnabled) return;
        if (!this._isPinching || event.touches.length !== 2) return;
        event.preventDefault();

        const currentDistance = this.getTouchDistance(event.touches[0], event.touches[1]);
        const delta = this._lastPinchDistance - currentDistance;
        this._lastPinchDistance = currentDistance;

        // Standard mobile convention: pinch-OUT (fingers apart) = zoom IN, pinch-IN = zoom OUT
        // delta = last - current, so: pinch-IN → positive delta → positive wheelDelta (zoom out)
        const wheelDelta = delta * 3;
        Logger.Instance.Info(`pinch delta=${delta.toFixed(2)} wheelDelta=${wheelDelta.toFixed(2)} dist=${currentDistance.toFixed(0)}`, 'Pinch');
        InputEventManager.Instance.mouseWheelDelta = wheelDelta;

        this.eventManager.emit('input:wheel', {
            type: 'wheel',
            deltaX: 0,
            deltaY: wheelDelta,
            deltaZ: 0,
            wheelDelta,
            modifiers: { ctrl: false, shift: false, alt: false, meta: false },
            originalEvent: event
        });
    }

    private handleTouchEnd(event: TouchEvent): void {
        if (event.touches.length < 2) {
            this._isPinching = false;
            this._lastPinchDistance = 0;
        }
    }

    // ==================== CONFIGURATION ====================
    
    /**
     * Enable or disable input handling
     */
    public setEnabled(enabled: boolean): void {
        this.isEnabled = enabled;
        //Logger.Instance.Info(`⌨️ Input handling ${enabled ? 'enabled' : 'disabled'}`, 'InputManager');
    }
    
    /**
     * Check if input handling is enabled
     */
    public isInputEnabled(): boolean {
        return this.isEnabled;
    }
    
    /**
     * Add or update a key mapping
     */
    public setKeyMapping(key: string, action: string): void {
        this.keyMappings[key] = action;
        //Logger.Instance.Info(`🔑 Key mapping updated: ${key} -> ${action}`, 'InputManager');
    }
    
    /**
     * Remove a key mapping
     */
    public removeKeyMapping(key: string): void {
        delete this.keyMappings[key];
        //Logger.Instance.Info(`🔑 Key mapping removed: ${key}`, 'InputManager');
    }
    
    /**
     * Get all key mappings
     */
    public getKeyMappings(): { [key: string]: string } {
        return { ...this.keyMappings };
    }
    
    /**
     * Get action for a key
     */
    public getActionForKey(key: string): string | undefined {
        return this.keyMappings[key];
    }
    
    /**
     * Get keys mapped to an action
     */
    public getKeysForAction(action: string): string[] {
        return Object.keys(this.keyMappings).filter(key => this.keyMappings[key] === action);
    }
    
    // ==================== UTILITY METHODS ====================
    
    /**
     * Check if a key is currently pressed
     */
    public isKeyPressed(key: string): boolean {
        // This would require tracking key states, which could be added if needed
        return false;
    }
    
    /**
     * Get input statistics
     */
    public getStatistics(): {
        totalKeyMappings: number;
        isEnabled: boolean;
        availableActions: string[];
    } {
        const actions = Array.from(new Set(Object.values(this.keyMappings)));
        
        return {
            totalKeyMappings: Object.keys(this.keyMappings).length,
            isEnabled: this.isEnabled,
            availableActions: actions
        };
    }
    
    /**
     * Process camera controls (zoom and orbit)
     * This method was extracted from EngineDemoApp.ts
     */
    public processCameraControls(appSettings: any): void {
        // Stillgelegt: Zoom gehört ausschließlich EngineDemoApp.applyWheelZoom().
        // Dieser Pfad rechnete mit umgekehrtem Vorzeichen gegen den wheel-Listener an
        // und las den interpolierten statt den Zielwert (Alex-Report 2026-08-03).
        // this.handleZoomInput(appSettings);
        this.handleOrbitInput(appSettings);
    }
    
    /**
     * Handle zoom input from mouse wheel
     * EXTRACTED FROM EngineDemoApp.ts - original code
     */
    private handleZoomInput(appSettings: any): void {
        const inputManager = InputEventManager.Instance;
        
        // Check if there's mouse wheel input
        if (inputManager.mouseWheelDelta !== 0) {
            if (!appSettings?.cameraDistance) {
                Logger.Instance.Warn(`⚠️ Cannot zoom: appSettings.cameraDistance not available`, 'Input');
                return;
            }
            
            const wheelDelta = inputManager.mouseWheelDelta;
            const currentDistance = appSettings.cameraDistance.value ?? 3.8;
            
            // Calculate zoom factor
            const baseZoomSpeed = 0.1; // Base zoom amount per wheel turn
            const maxZoomPerTurn = 2.0; // Maximum zoom change per wheel turn
            
            // Reversed logic: positive wheelDelta = zoom in (decrease distance), negative = zoom out (increase distance)
            let zoomFactor = -wheelDelta * baseZoomSpeed * Math.min(currentDistance * 0.1, maxZoomPerTurn);
            
            // Make zoom in (positive wheelDelta) slightly slower than zoom out for better control
            if (wheelDelta > 0) { // Zooming in
                zoomFactor *= 0.5; // Half speed for zoom in
            }
            
            const newDistance = currentDistance + zoomFactor;
            
            // Clamp to bounds
            const minDistance = appSettings.cameraDistance.boundsMin ?? 0.2;
            const maxDistance = appSettings.cameraDistance.boundsMax ?? 20;
            const clampedDistance = Math.max(minDistance, Math.min(maxDistance, newDistance));
            
            // Only update if the value actually changed
            if (Math.abs(clampedDistance - currentDistance) > 0.001) {
                appSettings.cameraDistance.value = clampedDistance;
                //Logger.Instance.Debug(`🔍 Zoom: wheel=${wheelDelta.toFixed(0)}, distance=${currentDistance.toFixed(2)} → ${clampedDistance.toFixed(2)}`, 'Input');
            }
        }
    }
    
    /**
     * Handle orbit input from mouse drag
     * EXTRACTED FROM EngineDemoApp.ts - original code
     */
    private handleOrbitInput(appSettings: any): void {
        const inputManager = InputEventManager.Instance;
        
        // Check if mouse is being dragged and there's movement
        if (inputManager.isMouseDown && (inputManager.mouseDelta.x !== 0 || inputManager.mouseDelta.y !== 0)) {
            if (!appSettings?.cameraLatitude || !appSettings?.cameraLongitude) {
                Logger.Instance.Warn(`⚠️ Cannot orbit: camera lat/lon settings not available`, 'Input');
                return;
            }
            
            const deltaX = inputManager.mouseDelta.x;
            const deltaY = inputManager.mouseDelta.y;
            
            // Only process if there's actual movement
            if (Math.abs(deltaX) > 0.1 || Math.abs(deltaY) > 0.1) {
                // Current camera angles
                const currentLongitude = appSettings.cameraLongitude.value ?? 0;
                const currentLatitude = appSettings.cameraLatitude.value ?? -16;
                
                // Orbit sensitivity (how much camera moves per pixel)
                const orbitSensitivity = 1.0; // degrees per pixel
                
                // Calculate new angles
                // Horizontal mouse movement (deltaX) controls longitude (left/right orbit)
                // Vertical mouse movement (deltaY) controls latitude (up/down orbit)
                const newLongitude = currentLongitude - (deltaX * orbitSensitivity); // Inverted X for intuitive orbit
                const newLatitude = currentLatitude + (deltaY * orbitSensitivity); // Normal Y direction
                
                // Clamp to bounds
                const minLongitude = appSettings.cameraLongitude.boundsMin ?? -180;
                const maxLongitude = appSettings.cameraLongitude.boundsMax ?? 180;
                const minLatitude = appSettings.cameraLatitude.boundsMin ?? -180;
                const maxLatitude = appSettings.cameraLatitude.boundsMax ?? 180;
                
                const clampedLongitude = Math.max(minLongitude, Math.min(maxLongitude, newLongitude));
                const clampedLatitude = Math.max(minLatitude, Math.min(maxLatitude, newLatitude));
                
                // Only update if values actually changed
                let updated = false;
                if (Math.abs(clampedLongitude - currentLongitude) > 0.01) {
                    appSettings.cameraLongitude.value = clampedLongitude;
                    updated = true;
                }
                if (Math.abs(clampedLatitude - currentLatitude) > 0.01) {
                    appSettings.cameraLatitude.value = clampedLatitude;
                    updated = true;
                }
                
                if (updated) {
                    //Logger.Instance.Debug(`🔄 Orbit: mouse(${deltaX.toFixed(0)}, ${deltaY.toFixed(0)}) → lat=${clampedLatitude.toFixed(1)}°, lon=${clampedLongitude.toFixed(1)}°`, 'Input');
                }
            }
        }
    }
    
    /**
     * Cleanup and dispose resources
     */
    public dispose(): void {
        this.removeEventListeners();
        this.isEnabled = false;
        //Logger.Instance.Info('🧹 InputManager disposed', 'InputManager');
    }
} 